import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { fetchStockChartsData } from "../../apis/Stock";
import useDarkMode from "../../hooks/useDarkMode";
import { getChartTheme } from "../../utils/chartTheme";

const normalizeDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().split("T")[0];
};

const findClosestChartEntry = (value, entries) => {
  const normalizedDate = normalizeDate(value);
  if (!normalizedDate || !entries?.length) return null;

  const targetTime = new Date(normalizedDate).getTime();
  let closest = entries[0];

  for (let index = 1; index < entries.length; index += 1) {
    const candidate = entries[index];
    const candidateTime = new Date(candidate.date).getTime();
    const closestTime = new Date(closest.date).getTime();

    if (Math.abs(candidateTime - targetTime) < Math.abs(closestTime - targetTime)) {
      closest = candidate;
    }
  }

  return closest;
};

const formatAmount = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";

  const absValue = Math.abs(numeric);
  const sign = numeric < 0 ? "-" : "";

  if (absValue >= 1_000_000_000) {
    return `${sign}$${(absValue / 1_000_000_000).toFixed(1).replace(/\.0$/, "")}B`;
  }
  if (absValue >= 1_000_000) {
    return `${sign}$${(absValue / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  }
  if (absValue >= 1_000) {
    return `${sign}$${(absValue / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  }

  return `${sign}$${absValue.toFixed(0)}`;
};

export default function StockLineChartInsider({ ticker, companykor, onDataLoad, chartSelection }) {
  const [chartSource, setChartSource] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const isDarkMode = useDarkMode();
  const chartColors = useMemo(() => getChartTheme(isDarkMode), [isDarkMode]);

  const stockStyle = isDarkMode
    ? { line: "#34d399", area: "rgba(52, 211, 153, 0.12)" }
    : { line: "#16a34a", area: "rgba(22, 163, 74, 0.12)" };

  const highlightStyle = useMemo(() => {
    const isSell = chartSelection?.status === "sell";
    return isSell ? "#1a1de2" : "#dc3545";
  }, [chartSelection]);

  const amountBarColor = useMemo(() => {
    const isSell = chartSelection?.status === "sell";
    return isSell
      ? (isDarkMode ? "rgba(96, 165, 250, 0.58)" : "rgba(37, 99, 235, 0.5)")
      : (isDarkMode ? "rgba(251, 113, 133, 0.58)" : "rgba(220, 53, 69, 0.48)");
  }, [chartSelection, isDarkMode]);

  useEffect(() => {
    if (!ticker) return;

    let isMounted = true;
    setIsLoading(true);

    fetchStockChartsData(ticker)
      .then((res) => res.json())
      .then((data) => {
        if (!isMounted) return;

        const sortedData = Array.isArray(data)
          ? [...data].sort((a, b) => new Date(a.date) - new Date(b.date))
          : [];

        setChartSource(sortedData);
        if (onDataLoad) {
          onDataLoad(sortedData.map((entry) => entry.date));
        }
      })
      .catch((err) => console.error("Error fetching line chart (Insider):", err))
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [ticker, onDataLoad]);

  const allInsiderPoints = useMemo(() => {
    if (!chartSelection?.barTrades?.length || chartSource.length === 0) return [];

    const grouped = new Map();

    chartSelection.barTrades.forEach((trade) => {
      const closest = findClosestChartEntry(trade.date, chartSource);
      if (!closest) return;

      const key = normalizeDate(closest.date);
      if (!key || grouped.has(key)) return;

      grouped.set(key, {
        x: new Date(closest.date).getTime(),
        y: closest.close,
      });
    });

    return Array.from(grouped.values());
  }, [chartSelection, chartSource]);

  const highlightPoints = useMemo(() => {
    if (!chartSelection?.selectedTrades?.length || chartSource.length === 0) return [];

    const grouped = new Map();

    chartSelection.selectedTrades.forEach((trade) => {
      const closest = findClosestChartEntry(trade.date, chartSource);
      if (!closest) return;

      const key = normalizeDate(closest.date);
      if (!key || grouped.has(key)) return;

      grouped.set(key, {
        x: new Date(closest.date).getTime(),
        y: closest.close,
      });
    });

    return Array.from(grouped.values());
  }, [chartSelection, chartSource]);

  const amountBarSeriesData = useMemo(() => {
    if (!chartSelection?.barTrades?.length || chartSource.length === 0) return [];

    const groupedValues = {};

    chartSelection.barTrades.forEach((trade) => {
      const closest = findClosestChartEntry(trade.date, chartSource);
      const amount = Number(trade.transactionamount ?? trade.totvalue ?? trade.totalvalue);
      if (!closest || !Number.isFinite(amount) || amount <= 0) return;

      const alignedDate = normalizeDate(closest.date);
      groupedValues[alignedDate] = (groupedValues[alignedDate] || 0) + amount;
    });

    return Object.entries(groupedValues)
      .sort((a, b) => new Date(a[0]) - new Date(b[0]))
      .map(([date, amount]) => ({
        x: new Date(date).getTime(),
        y: amount,
      }));
  }, [chartSelection, chartSource]);

  const selectedRange = useMemo(() => {
    if (chartSource.length === 0) {
      return { min: undefined, max: undefined };
    }

    const firstTime = new Date(chartSource[0].date).getTime();
    const lastTime = new Date(chartSource[chartSource.length - 1].date).getTime();
    const latest = new Date(lastTime);
    const oneYearAgo = new Date(latest);
    oneYearAgo.setFullYear(latest.getFullYear() - 1);
    const defaultMin = Math.max(oneYearAgo.getTime(), firstTime);

    if (!highlightPoints.length) {
      return { min: defaultMin, max: lastTime };
    }

    const width = lastTime - defaultMin;
    const center = highlightPoints[highlightPoints.length - 1].x;
    const half = width / 2;
    let min = center - half;
    let max = center + half;

    if (min < firstTime) {
      min = firstTime;
      max = Math.min(firstTime + width, lastTime);
    }

    if (max > lastTime) {
      max = lastTime;
      min = Math.max(lastTime - width, firstTime);
    }

    return { min, max };
  }, [chartSource, highlightPoints]);

  const mainChartId = `chart-insider-main-${ticker}`;

  const series = useMemo(() => ([
    {
      name: `${companykor} 주가`,
      type: "area",
      data: chartSource.map((entry) => ({
        x: new Date(entry.date).getTime(),
        y: entry.close,
      })),
    },
    {
      name: "전체 거래액",
      type: "bar",
      data: amountBarSeriesData,
    },
  ]), [amountBarSeriesData, chartSource, companykor]);

  const brushSeries = useMemo(() => ([
    {
      name: `${companykor} 주가`,
      type: "area",
      data: chartSource.map((entry) => ({
        x: new Date(entry.date).getTime(),
        y: entry.close,
      })),
    },
  ]), [chartSource, companykor]);

  const pointAnnotations = useMemo(() => ([
    ...allInsiderPoints.map((point, index) => ({
      x: point.x,
      y: point.y,
      marker: {
        size: 5,
        fillColor: isDarkMode ? "#6b7280" : "#9ca3af",
        strokeColor: isDarkMode ? "#111827" : "#ffffff",
        strokeWidth: 1,
      },
      label: {
        show: false,
      },
      id: `insider-point-${index}`,
    })),
    ...highlightPoints.map((point, index) => ({
      x: point.x,
      y: point.y,
      marker: {
        size: 6,
        fillColor: highlightStyle,
        strokeColor: isDarkMode ? "#111827" : "#ffffff",
        strokeWidth: 2,
      },
      label: {
        show: false,
      },
      id: `selected-point-${index}`,
    })),
  ]), [allInsiderPoints, highlightPoints, highlightStyle, isDarkMode]);

  const options = useMemo(() => ({
    chart: {
      id: mainChartId,
      type: "line",
      stacked: false,
      toolbar: { show: false },
      zoom: { enabled: false },
      selection: { enabled: false },
      animations: { enabled: false },
      background: "transparent",
    },
    colors: [
      stockStyle.line,
      amountBarColor,
    ],
    stroke: {
      curve: "smooth",
      width: [2, 0],
    },
    fill: {
      type: ["gradient", "solid"],
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.45,
        opacityTo: 0.05,
        stops: [0, 100],
      },
    },
    dataLabels: {
      enabled: false,
    },
    plotOptions: {
      bar: {
        columnWidth: "52%",
        borderRadius: 2,
      },
    },
    xaxis: {
      type: "datetime",
      min: selectedRange.min,
      max: selectedRange.max,
      labels: {
        style: {
          colors: chartColors.tickColor,
          fontSize: "11px",
        },
        format: "yy.MM",
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false },
      tickAmount: 8,
    },
    yaxis: [
      {
        seriesName: `${companykor} 주가`,
        labels: {
          offsetX: -10,
          style: {
            colors: chartColors.tickColor,
            fontSize: "11px",
          },
          formatter: (value) => Number(value).toFixed(1),
        },
      },
      {
        seriesName: "전체 거래액",
        opposite: true,
        labels: {
          style: {
            colors: chartColors.tickColor,
            fontSize: "11px",
          },
          offsetX: -20,
          formatter: (value) => formatAmount(value),
        },
      },
    ],
    grid: {
      borderColor: chartColors.gridColor,
      strokeDashArray: 4,
      padding: {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
      },
    },
    annotations: {
      points: pointAnnotations,
    },
    tooltip: {
      shared: true,
      intersect: false,
      followCursor: true,
      theme: isDarkMode ? "dark" : "light",
      x: {
        format: "yyyy.MM.dd",
      },
      y: {
        formatter: (value, { seriesIndex }) => {
          if (seriesIndex === 1) {
            return formatAmount(value);
          }

          return `$${Number(value).toLocaleString()}`;
        },
      },
    },
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "center",
      labels: {
        colors: chartColors.legendColor,
      },
    },
    noData: {
      text: "데이터가 없습니다.",
      style: {
        color: chartColors.tickColor,
      },
    },
  }), [amountBarColor, chartColors.gridColor, chartColors.legendColor, chartColors.tickColor, companykor, isDarkMode, mainChartId, pointAnnotations, selectedRange.max, selectedRange.min, stockStyle.line]);

  const brushOptions = useMemo(() => ({
    chart: {
      type: "area",
      height: 90,
      background: "transparent",
      toolbar: { show: false },
      animations: { enabled: false },
      brush: {
        target: mainChartId,
        enabled: true,
      },
      selection: {
        enabled: true,
        xaxis: {
          min: selectedRange.min,
          max: selectedRange.max,
        },
        fill: {
          color: isDarkMode ? "rgba(148, 163, 184, 0.2)" : "rgba(100, 116, 139, 0.12)",
          opacity: 1,
        },
        stroke: {
          color: isDarkMode ? "rgba(226, 232, 240, 0.45)" : "rgba(100, 116, 139, 0.3)",
          opacity: 1,
          width: 1,
          dashArray: 3,
        },
      },
    },
    colors: [isDarkMode ? "#64748b" : "#94a3b8"],
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.28,
        opacityTo: 0.08,
        stops: [0, 100],
      },
    },
    stroke: {
      curve: "smooth",
      width: 1.5,
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      type: "datetime",
      labels: {
        style: {
          colors: chartColors.tickColor,
          fontSize: "10px",
        },
        formatter: (_, timestamp) => {
          const date = new Date(timestamp);
          const quarter = Math.floor(date.getMonth() / 3) + 1;
          return `${String(date.getFullYear()).slice(2)}.Q${quarter}`;
        },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false },
    },
    yaxis: [
      {
        labels: {
          show: true,
          minWidth: 25,
          formatter: () => "",
        },
      },
      {
        opposite: true,
        labels: {
          show: true,
          formatter: () => "",
        },
      },
    ],
    grid: {
      show: false,
      padding: {
        left: 0,
        right: 0,
      },
    },
    legend: {
      show: false,
    },
  }), [chartColors.tickColor, isDarkMode, mainChartId, selectedRange.max, selectedRange.min]);

  if (isLoading) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted" style={{ minHeight: "300px" }}>
        <div className="spinner-border spinner-border-sm text-primary mb-2" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div style={{ fontSize: "13px" }}>데이터를 불러오는 중입니다...</div>
      </div>
    );
  }

  if (chartSource.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center text-muted border border-secondary border-dashed h-100" style={{ borderStyle: "dashed", minHeight: "300px" }}>
        데이터가 없습니다.
      </div>
    );
  }

  return (
    <div className="w-100 h-100">
      <Chart
        options={options}
        series={series}
        type="line"
        height="380"
      />
      <div style={{ marginTop: "10px" }}>
        <Chart
          options={brushOptions}
          series={brushSeries}
          type="area"
          height="90"
        />
      </div>
    </div>
  );
}
